import PropTypes from "prop-types";
import Datatable from "@components/datatable";
import StatusChip from "@components/ui-components/status-chip";
import PaginationBar from "@components/datatable/pagination-bar";
import usePaymentHistory from "./use-payment-history";
import { formatCurrency } from "@src/utility/helpers/utils";
import { formatDate } from "@src/utility/helpers/date";

const STATUS_TONE = { disbursed: "success" };
const STATUS_LABEL = { disbursed: "Disbursed" };

const ROWS_PER_PAGE_OPTIONS = [10, 20, 50];

const PaymentHistory = ({ brandId, isSelfPayout = false }) => {
    const { history, pagination, loading, handlePageChange, handlePerRowsChange } = usePaymentHistory(brandId);

    // A self-payout brand pays its publishers against their invoices directly — there
    // is no debit note in the chain, so the column carries the invoice instead.
    const documentColumn = isSelfPayout
        ? {
            Header  : "Invoice",
            accessor: "invoice_number",
            Cell    : ({ value, row }) => <span className="text-gray-900">{value || row.debit_note_number || "—"}</span>
        }
        : { Header: "Debit note", accessor: "debit_note_number", Cell: ({ value }) => <span className="text-gray-900">{value}</span> };

    const columns = [
        { Header: "Publisher", accessor: "holder_name", Cell: ({ value }) => <span className="font-medium text-gray-900">{value}</span> },
        documentColumn,
        { Header: "Period", accessor: "payout_period", Cell: ({ value }) => <span className="text-gray-700">{value ? formatDate(value) : "—"}</span> },
        { Header: "Amount", accessor: "amount", Cell: ({ value, row }) => <span className="text-gray-900">{formatCurrency(value, row.currency)}</span> },
        { Header: "Status", accessor: "status", Cell: ({ value }) => <StatusChip tone={STATUS_TONE[value] || "neutral"} label={STATUS_LABEL[value] || value} /> }
    ];

    return (
        <div>
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                <Datatable
                    columns={columns}
                    data={history}
                    loading={loading}
                    showPagination={false}
                    customNoData={<span className="text-sm text-muted">No disbursed {isSelfPayout ? "invoices" : "debit notes"} yet.</span>} />
            </div>

            {pagination.total > pagination.limit && (
                <div className="mt-2">
                    <PaginationBar
                        pageIndex={pagination.page - 1}
                        pageSize={pagination.limit}
                        total={pagination.total}
                        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                        onPageChange={(pageIndex) => handlePageChange(pagination.limit, pageIndex + 1)}
                        onRowsPerPageChange={handlePerRowsChange} />
                </div>
            )}
        </div>
    );
};

PaymentHistory.propTypes = {
    brandId     : PropTypes.string.isRequired,
    isSelfPayout: PropTypes.bool
};

export default PaymentHistory;
